import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';

import {
  ROLE_ADMIN,
  ROLE_SALARY_ADMIN,
  ROLE_SALARY_VIEW,
  useAuth
} from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';
import { Collaborator } from '@/models';
import { fetchCollaboratorSalary } from '@/features/Collaborators/Services/salarySubcollectionService';

type CollaboratorsLayoutContext = {
  collaborators: Collaborator[];
  isLoading: boolean;
  error: Error | null;
};

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (value?: any) => {
  if (!value) return '-';
  const seconds = value?.seconds ?? value?._seconds;
  if (seconds) {
    const date = new Date(seconds * 1000);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    if (typeof value === 'string' && value.trim()) return value;
    return '-';
  }
  return date.toLocaleDateString('pt-BR');
};

export default function CollaboratorSalaryPage() {
  const { collaborators, isLoading, error } = useOutletContext<CollaboratorsLayoutContext>();
  const { id } = useParams<{ id: string }>();
  const { user, userHasRole } = useAuth();
  const collaborator = useMemo(
    () => collaborators.find((item) => item.id === id),
    [collaborators, id],
  );

  const [salaryData, setSalaryData] = useState<any | null>(null);
  const [isSalaryLoading, setIsSalaryLoading] = useState(false);

  const canSeePage =
    userHasRole(ROLE_ADMIN) ||
    userHasRole(ROLE_SALARY_ADMIN) ||
    userHasRole(ROLE_SALARY_VIEW);

  useEffect(() => {
    let cancelled = false;
    const fetchSalary = async () => {
      if (!collaborator?.id) return;
      setIsSalaryLoading(true);
      try {
        const salary = await fetchCollaboratorSalary(collaborator.id);
        if (!cancelled) setSalaryData(salary);
      } catch (err) {
        console.error('[CollaboratorSalaryPage] Failed to fetch salary', err);
      } finally {
        if (!cancelled) setIsSalaryLoading(false);
      }
    };
    fetchSalary();
    return () => {
      cancelled = true;
    };
  }, [collaborator?.id]);

  if (!canSeePage) {
    return (
      <div className="rounded-xl border border-amber-100 bg-amber-50 p-6 text-amber-900">
        Você não tem permissão para visualizar salários.
      </div>
    );
  }

  if (isLoading && !collaborators.length) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-red-800">
        Não foi possível carregar o colaborador.
      </div>
    );
  }

  if (!collaborator) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-700 shadow-sm">
        Colaborador não encontrado.
      </div>
    );
  }

  const currentSalary = (salaryData as any)?.currentEntry ?? null;
  const upcomingSalary = (salaryData as any)?.upcomingEntry ?? null;
  const history = (salaryData as any)?.history ?? [];

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-slate-50 p-4 md:p-6 shadow-[0_40px_120px_-60px_rgba(45,55,72,0.35)]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-700">Remuneração</p>
              <p className="text-xl font-semibold text-slate-900">Detalhamento</p>
            </div>
            <Link
              to={`/collaborators/${collaborator.id}`}
              className="rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-200"
            >
              Voltar para o perfil
            </Link>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-2">
                Último salário
              </p>
              {isSalaryLoading ? (
                <p className="text-sm text-slate-500">Carregando...</p>
              ) : (
                <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Valor</dt>
                    <dd className="text-lg font-semibold text-slate-900">{formatCurrency(currentSalary?.amount)}</dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Extras (Caju)</dt>
                    <dd className="text-lg font-semibold text-slate-900">{formatCurrency(currentSalary?.extras)}</dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Tipo</dt>
                    <dd className="text-sm font-semibold text-slate-900">
                      {currentSalary?.salaryType ?? currentSalary?.currentSalaryType ?? '-'}
                    </dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Data efetiva</dt>
                    <dd className="text-sm font-semibold text-slate-900">{formatDate(currentSalary?.changeDate)}</dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Motivo</dt>
                    <dd className="text-sm font-semibold text-slate-900">{currentSalary?.changeReason || '-'}</dd>
                  </div>
                </dl>
              )}
            </div>

            {upcomingSalary ? (
              <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-2">
                  Próximo salário agendado
                </p>
                <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Valor</dt>
                    <dd className="text-lg font-semibold text-slate-900">{formatCurrency(upcomingSalary.amount)}</dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Extras (Caju)</dt>
                    <dd className="text-lg font-semibold text-slate-900">{formatCurrency(upcomingSalary.extras)}</dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Tipo</dt>
                    <dd className="text-sm font-semibold text-slate-900">{upcomingSalary.salaryType}</dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Data efetiva</dt>
                    <dd className="text-sm font-semibold text-slate-900">{formatDate(upcomingSalary.changeDate)}</dd>
                  </div>
                  <div className="rounded-xl bg-slate-50 px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Motivo</dt>
                    <dd className="text-sm font-semibold text-slate-900">{upcomingSalary.changeReason || '-'}</dd>
                  </div>
                </dl>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Histórico</p>
                {isSalaryLoading ? <span className="text-xs text-slate-500">Carregando...</span> : null}
              </div>
              <div className="mt-3 overflow-auto rounded-xl border border-slate-100">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      <th className="px-4 py-2 font-semibold text-slate-700">Data</th>
                      <th className="px-4 py-2 font-semibold text-slate-700">Tipo</th>
                      <th className="px-4 py-2 font-semibold text-slate-700">Valor</th>
                      <th className="px-4 py-2 font-semibold text-slate-700">Extras</th>
                      <th className="px-4 py-2 font-semibold text-slate-700">Motivo</th>
                      <th className="px-4 py-2 font-semibold text-slate-700">Observações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {history.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 text-sm text-slate-500">
                          Sem histórico de salário.
                        </td>
                      </tr>
                    ) : (
                      history.map((entry: any) => (
                        <tr key={entry.id}>
                          <td className="px-4 py-2 text-slate-800">{formatDate(entry.changeDate)}</td>
                          <td className="px-4 py-2 text-slate-800">{entry.salaryType}</td>
                          <td className="px-4 py-2 text-slate-800">{formatCurrency(entry.amount)}</td>
                          <td className="px-4 py-2 text-slate-800">{formatCurrency(entry.extras)}</td>
                          <td className="px-4 py-2 text-slate-800">{entry.changeReason || '-'}</td>
                          <td className="px-4 py-2 text-slate-800">{entry.changeObservations || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

